/**
 * Shared Utilities for Best-Fit Processing
 *
 * Common functions and utilities used across all best-fit strategies.
 * Extracted from candidate-selector.ts to eliminate duplication and provide
 * reusable functionality for the strategy pattern implementation.
 */

import { BondCategorizationEngine } from '~/engine/processing/shared/bond-categorization';
import type { BondSeries, Candidate, Configuration } from '~/types';
import { ComparisonEngine } from './comparison-engine';

/**
 * Candidate ranking information with metrics
 */
export interface CandidateRanking {
  candidate: Candidate;
  rank: number;
  score: number;
  isViable: boolean;
  metrics: {
    totalPrincipal: number;
    totalInterest: number;
    revenueUtilization: number;
    efficiency: number;
    interestRatio: number;
  };
}

/**
 * Sorting function type for candidate rankings
 */
export type CandidateSortFunction = (a: CandidateRanking, b: CandidateRanking) => number;

/**
 * Creates a ranking object for a candidate with all relevant metrics.
 */
export function createCandidateRanking(candidate: Candidate, index: number): CandidateRanking {
  const { results } = candidate;

  return {
    candidate,
    rank: index + 1, // Will be updated after sorting
    score: 0, // Will be calculated based on strategy
    isViable: results.isViable,
    metrics: {
      totalPrincipal: results.totalPrincipal,
      totalInterest: results.totalInterest,
      revenueUtilization: results.revenueUtilization,
      efficiency: results.efficiency || 0,
      interestRatio: results.interestRatio,
    },
  };
}

/**
 * Max-value strategy comparator function.
 * @deprecated Use ComparisonEngine.sortByUnlimitedStrategy instead
 */
export const sortByMaxValueStrategy: CandidateSortFunction = (a, b) => {
  return ComparisonEngine.sortByUnlimitedStrategy(a, b);
};

/**
 * Target-value strategy comparator function.
 * @deprecated Use ComparisonEngine.sortByTargetedStrategy or sortByMixedStrategy based on bond mix
 */
export const sortByTargetValueStrategy: CandidateSortFunction = (a, b) => {
  return ComparisonEngine.sortByTargetedStrategy(a, b);
};

/**
 * Orders candidates using a specific sorting strategy.
 * Returns candidates in descending order of preference (best first).
 */
export function orderCandidates(
  candidates: Candidate[],
  sortFunction: CandidateSortFunction
): Candidate[] {
  if (candidates.length === 0) return [];

  // Create rankings for all candidates
  const rankings = candidates.map((candidate, index) => createCandidateRanking(candidate, index));

  // Sort using provided strategy and update ranks
  const sortedRankings = rankings.sort(sortFunction).map((ranking, index) => ({
    ...ranking,
    rank: index + 1,
  }));

  return sortedRankings.map((ranking) => ranking.candidate);
}

/**
 * Gets detailed ranking information for debugging and analysis.
 */
export function getCandidateRankings(
  candidates: Candidate[],
  sortFunction: CandidateSortFunction
): CandidateRanking[] {
  if (candidates.length === 0) return [];

  const rankings = candidates.map((candidate, index) => createCandidateRanking(candidate, index));
  return rankings.sort(sortFunction).map((ranking, index) => ({
    ...ranking,
    rank: index + 1,
  }));
}

/**
 * Logs candidate ranking information for debugging.
 */
export function logCandidateRankings(
  candidates: Candidate[],
  sortFunction: CandidateSortFunction,
  title: string = 'Candidate Rankings',
  strategyName: string = 'Unknown'
): void {
  const rankings = getCandidateRankings(candidates, sortFunction);

  console.log(`\n📊 ${title} (${strategyName} Strategy)`);
  console.log('=====================================');

  rankings.forEach((ranking) => {
    const status = ranking.isViable ? '✅' : '❌';
    const metrics = ranking.metrics;

    console.log(`${ranking.rank}. ${status} ${ranking.candidate.name}`);
    console.log(`   Principal: $${metrics.totalPrincipal.toLocaleString()}`);
    console.log(`   Utilization: ${(metrics.revenueUtilization * 100).toFixed(2)}%`);
    console.log(`   Efficiency: ${(metrics.efficiency * 100).toFixed(2)}%`);
    console.log(`   Interest: $${metrics.totalInterest.toLocaleString()}`);
    console.log(`   Int. Ratio: ${(metrics.interestRatio * 100).toFixed(2)}%`);
  });

  console.log('=====================================\n');
}

/**
 * Checks if any candidate has target information in their bond series.
 * Used to determine project type automatically.
 */
export function hasTargetInformation(
  candidates: Candidate[],
  configuration: Configuration
): boolean {
  return candidates.some((candidate) =>
    BondCategorizationEngine.candidateHasTargetedBonds(candidate, configuration)
  );
}

/**
 * Filters candidates to only those from first-fit phase.
 */
export function getFirstFitCandidates(candidates: Candidate[]): Candidate[] {
  return candidates.filter((c) => c.createdInPhase === 'first-fit');
}

/**
 * Filters candidates to only those that are viable according to standard criteria.
 */
export function getStandardViableCandidates(candidates: Candidate[]): Candidate[] {
  return candidates.filter((c) => c.results.isViable);
}

/**
 * Gets the best candidate from a list using the specified sorting strategy.
 */
export function getBestCandidate(
  candidates: Candidate[],
  sortFunction: CandidateSortFunction
): Candidate | null {
  if (candidates.length === 0) return null;

  const orderedCandidates = orderCandidates(candidates, sortFunction);
  return orderedCandidates[0];
}

/**
 * Validates that candidates have the minimum required data for processing.
 */
export function validateCandidatesStructure(candidates: Candidate[]): string[] {
  const errors: string[] = [];

  if (!candidates || candidates.length === 0) {
    errors.push('No candidates provided');
    return errors;
  }

  candidates.forEach((candidate, index) => {
    if (!candidate.name) {
      errors.push(`Candidate ${index}: missing name`);
    }

    if (!candidate.results) {
      errors.push(`Candidate ${index}: missing results`);
    }

    // Removed: bondSeries check (now in configuration, not candidate)

    if (candidate.results && typeof candidate.results.isViable !== 'boolean') {
      errors.push(`Candidate ${index}: invalid viability status`);
    }
  });

  return errors;
}

/**
 * Creates a summary of candidates for logging purposes.
 */
export function createCandidatesSummary(candidates: Candidate[]): {
  total: number;
  firstFit: number;
  viable: number;
  withTargets: number;
} {
  const firstFitCandidates = getFirstFitCandidates(candidates);
  const viableCandidates = getStandardViableCandidates(candidates);
  // Note: hasTargetInformation now requires configuration parameter
  // This function may need to be updated to accept configuration
  const withTargets: Candidate[] = []; // TODO: Update when configuration is available

  return {
    total: candidates.length,
    firstFit: firstFitCandidates.length,
    viable: viableCandidates.length,
    withTargets: withTargets.length,
  };
}

/**
 * Checks if a specific bond has a fixed target (DOES_NOT_EXCEED).
 * @deprecated Use BondCategorizationEngine.isFixedTargetBond() instead
 */
export function isFixedTargetBond(bond: BondSeries): boolean {
  return BondCategorizationEngine.isFixedTargetBond(bond);
}

/**
 * Checks if a candidate has any flexible bonds (no target or ACCEPTS_OVERFLOW).
 * These are bonds that can accept additional principal during optimization.
 * @deprecated Use BondCategorizationEngine.candidateHasFlexibleBonds() instead
 */
export function hasFlexibleBonds(candidate: Candidate, configuration: Configuration): boolean {
  return BondCategorizationEngine.candidateHasFlexibleBonds(candidate, configuration);
}

/**
 * Gets the IDs of all flexible bonds (those that can accept additional principal).
 * @deprecated Use BondCategorizationEngine.getCandidateFlexibleBondIds() instead
 */
export function getFlexibleBondIds(candidate: Candidate, configuration: Configuration): string[] {
  return BondCategorizationEngine.getCandidateFlexibleBondIds(candidate, configuration);
}

/**
 * Checks if all bonds in a candidate have fixed targets (DOES_NOT_EXCEED).
 * @deprecated Use BondCategorizationEngine with configuration.bondSeries instead
 */
export function hasOnlyFixedTargetBonds(
  _candidate: Candidate,
  configuration: Configuration
): boolean {
  if (!configuration.bondSeries || configuration.bondSeries.length === 0) {
    return false;
  }

  return BondCategorizationEngine.hasOnlyFixedTargets(configuration.bondSeries);
}
